import { AxiosError } from "axios";
import moment from "moment";
import { useParams } from "react-router-dom";
import { BillingDetail, CtBillType, TaxPurposeType } from "src/api/billingSchedule/billing-type";
import {
  getPopbillTaxInvoiceViewUrlAsync,
  mntBillSendEmail,
  mntBillUpdate,
  mntGetWeekDay,
  sendTaxInvoiceEmailAsync,
  sendTaxInvoiceToNtsAsync,
} from "src/api/billingSchedule/billingSchedule-api";
import { ContractorNoti } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import Link from "src/components/PartnerLink";
import pagePath from "src/pagePath.json";
import { useErrorModal } from "src/recoil/errorModal/hook";
import { useModal } from "src/recoil/modalState/hook";
import BillTaxPurposeModal from "../detail/components/BillTaxPurposeModal";
import TaxInvocieDetailSection from "../detail/components/TaxInvocieDetailSection";

/**
 * 팝빌 오류 정보 파싱
 * errorData 는 JSON 문자열로 내려오는 경우와 객체로 내려오는 경우가 혼재되어 있어 둘 다 처리한다.
 */
const parsePopbillError = (errorData: any): { popbillCode?: string; popbillMessage?: string } => {
  if (!errorData) return {};

  try {
    return typeof errorData === "string" ? JSON.parse(errorData) : errorData;
  } catch {
    return {};
  }
};

type Props = {
  contractBaseInfo: any;
  scheduleId?: string | number;
  onSuccess?: (scheduleId?: string | number) => void;
};

const useBill = ({ contractBaseInfo, scheduleId, onSuccess }: Props) => {
  const { id: contractId } = useParams();
  const { setBaseModal, setAbstractModalZ1 } = useModal();
  const { openErrorModal } = useErrorModal();

  // 국세청 전자세금계산서 발행
  // eCT408(팝빌 오류) 케이스에 별도 문구를 노출해야 해서 공통 에러 처리를 끄고 직접 분기한다.
  const { executeAsync: sendNts } = useApiOperation(sendTaxInvoiceToNtsAsync, { noErrorModal: true });

  // 청구서 이메일 발송
  const { executeAsync: sendBillEmail } = useApiOperation(mntBillSendEmail);

  // 세금계산서 이메일 발송
  const { executeAsync: sendTaxInvoiceEmail } = useApiOperation(sendTaxInvoiceEmailAsync);

  // 청구서 수정
  const { executeAsync: updateBillTaxPurpose } = useApiOperation(mntBillUpdate);

  // 영업일 체크
  const { executeAsync: checkBusinessDay } = useApiOperation(mntGetWeekDay);

  // 팝빌 세금계산서 팝업 URL 조회
  const { executeAsync: getPopbillTaxInvoiceViewUrl } = useApiOperation(getPopbillTaxInvoiceViewUrlAsync);

  // 발행된 팝빌 세금계산서 팝업 URL 오픈
  const openTaxInvoiceViewUrl = async (mgtKey: string) => {
    // 팝빌 세금계산서 API가 유효할 때 가능 (true일 때만 가능)
    const isVerifiedTaxInvoice = contractBaseInfo?.provider.provider.isVerifiedTaxInvoice;

    if (isVerifiedTaxInvoice) {
      const { data } = await getPopbillTaxInvoiceViewUrl({ mgtKey });
      window.open(data.data.viewUrl, "_blank");
    }
  };

  // 세금계산서 발행 타입 체크 > 즉시발행 (+ 가산세 부과), 예약발행,
  const determineIssueType = async (taxInvoiceIssueDate: string) => {
    // 오늘 날짜
    let taxInvoiceIssueType = "immediate";

    const today = moment();

    // 세금계산서 작성일자
    const taxIssueDate = moment(taxInvoiceIssueDate);

    // 작성일이 미래면 예약발행
    if (taxIssueDate.isAfter(today, "day")) {
      return (taxInvoiceIssueType = "reserved");
    }

    // 작성일이 오늘이거나 과거인 경우 → 가산세 기준일 확인 필요
    // 임시 국세청 발급기한: 작성일 + 1달 + 10일

    const taxIssueDeadline = moment(taxIssueDate.format("YYYY.MM")).add(1, "months").add(9, "days").endOf("day");

    // 영업일 API 호출하여 실제 마감일 확인
    const { data } = await checkBusinessDay({ checkDate: taxIssueDeadline.format("YYYYMMDD") });

    const businessDay = data?.data?.weekday;
    if (businessDay) {
      const deadlineDate = moment(businessDay, "YYYYMMDD").endOf("day");
      // 오늘이 실제 마감일을 지났으면 가산세 부과
      if (today.isAfter(deadlineDate)) {
        return (taxInvoiceIssueType = "overdue");
      }
    }
    // 그 외는 즉시발행
    return taxInvoiceIssueType;
  };

  //
  // 세금계산서 발행 모달 열기
  const openNtsModal = async (bill: CtBillType, invoiceType: "TAX_PLUS" | "TAX_NONE") => {
    const { billId } = bill;

    // 세금계산서 자동 발행 여부 체크
    const isAutoTaxInvoice = contractBaseInfo?.scheduleList?.find((item: BillingDetail) => item.scheduleId === scheduleId)?.isAutoTaxInvoice;

    // 세금계산서 발행 타입 체크
    const taxInvoiceIssueType = (await determineIssueType(bill.taxInvoiceIssueDate)) || "immediate";

    // 세금계산서 발행 API 호출
    const handleTaxInvoiceSubmit = async () => {
      try {
        await sendNts({ billId, invoiceType });
      } catch (error) {
        const errorResponse = error instanceof AxiosError ? error.response : undefined;
        const errorCode = errorResponse?.data?.meta?.errorCode ?? "";
        const statusCode = errorResponse?.status ?? 0;

        // eCT408(팝빌 API 오류)는 모달 문구만으로 1차 원인 파악이 가능하도록 자사 문구 + 팝빌 응답을 함께 노출한다.
        if (errorCode === "eCT408") {
          const { popbillCode, popbillMessage } = parsePopbillError(errorResponse?.data?.meta?.errorData);

          const errorMessage = [
            `세금계산서 발행에 실패했습니다. [${errorCode}]`,
            popbillMessage ? `팝빌 응답: ${popbillMessage}${popbillCode ? `(${popbillCode})` : ""}` : "",
          ]
            .filter(Boolean)
            .join("\n\n");

          // errorCode 를 넘기면 useErrorModal 이 에러코드 카탈로그 문구로 errorMessage 를 덮어쓰고
          // 모달 하단에 [eCT408] 이 중복 노출되므로 넘기지 않는다.
          openErrorModal({ statusCode, errorMessage, align: "left", cause: error });
          return;
        }

        openErrorModal({ errorCode, statusCode, cause: error });
        return;
      }

      setAbstractModalZ1({ isOpen: false });
      setBaseModal({
        isOpen: true,
        title: "발행 요청되었습니다.",
        btnRightTitle: "확인",
        onClick: () => {
          setBaseModal({ isOpen: false });
          onSuccess?.(scheduleId);
        },
      });
    };

    if (taxInvoiceIssueType === "reserved" && !isAutoTaxInvoice) {
      const path = pagePath.contract.detail.replace(":id", String(contractId)) + `?tab=contract#세금계산서 설정`;

      setBaseModal({
        isOpen: true,
        title: "계산서 발행 예약을 할 수 없습니다.",
        children: (
          <>
            <div>
              <Link className="text-underline" to={path} target="_blank">
                자동 발행
              </Link>
              <div className="ic-target-blank"></div>
              <span>
                을 [사용]으로 설정해주세요. <br /> 또는 [계산서 작성일자] 이후에 다시 시도해주세요.
              </span>
            </div>
          </>
        ),
        btnRightTitle: "확인",
        onClick: () => {
          setBaseModal({ isOpen: false });
        },
      });
      return;
    }

    setAbstractModalZ1({
      isOpen: true,
      children: (
        <TaxInvocieDetailSection
          bill={bill} //
          taxInvoiceIssueType={taxInvoiceIssueType as "immediate" | "reserved" | "overdue"}
          onClose={() => setAbstractModalZ1({ isOpen: false })}
          onSubmit={handleTaxInvoiceSubmit}
        />
      ),
    });
  };

  //
  // 빌링 > 이용료 빌링현황 > 청구/영수 계산서 수정
  const handleEditTaxPurposeType = async (data: { scheduleId: string; taxPurposeType: TaxPurposeType; billId: string }) => {
    const { scheduleId, taxPurposeType, billId } = data;

    const onChangeTaxPurposeType = async (taxPurposeReqeust: TaxPurposeType) => {
      try {
        await updateBillTaxPurpose({ scheduleId, body: { taxPurposeType: taxPurposeReqeust, billId } });
        onSuccess?.(scheduleId);
      } catch (error) {
        console.error("청구/영수 수정 에러:", error);
      }
    };

    setAbstractModalZ1({
      isOpen: true,
      size: "medium",
      children: (
        <BillTaxPurposeModal
          taxPurposeType={taxPurposeType}
          onClose={() => setAbstractModalZ1({ isOpen: false })}
          onSubmit={(request: TaxPurposeType) => onChangeTaxPurposeType(request)}
        />
      ),
    });
  };

  // 세금 계산서 이메일 발송 (배열 처리)
  const sendingTaxInvoiceEmail = (data: { billId: string; mgtKeys: string[]; invoiceType: "TAX_PLUS" | "TAX_NONE" }) => {
    const { billId, mgtKeys, invoiceType = "TAX_PLUS" } = data;
    const path = pagePath.contract.detail.replace(":id", String(contractId)) + `?tab=contract#세금계산서 설정`;

    const taxInvoiceContactList = contractBaseInfo?.contract?.billContactList?.filter(
      (item: ContractorNoti) => item.receiveNotiType === "TAX_INVOICE",
    );
    setBaseModal({
      isOpen: true,
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: async () => {
        setBaseModal({ isOpen: false });

        try {
          // 병렬 처리
          await Promise.all(mgtKeys.map((mgtKey) => sendTaxInvoiceEmail({ billId, mgtKey })));

          setBaseModal({
            isOpen: true,
            title: "발송 요청되었습니다.",
            btnRightTitle: "확인",
            onClick: () => {
              setBaseModal({ isOpen: false });
              onSuccess?.(scheduleId);
            },
          });
        } catch (error) {
          console.error("이메일 발송 실패:", error);
        }
      },
      title: `${invoiceType === "TAX_NONE" ? "계산서" : "세금계산서"} 이메일을 다시 보내시겠습니까?`,
      children: (
        <>
          <div className="mb20">
            <Link className="text-underline" to={path} target="_blank">
              {"세금계산서 설정>이메일"}
            </Link>
            <div className="ic-target-blank"></div>
            <span>의 정보를 확인해주세요.</span>
          </div>
          {taxInvoiceContactList.length > 0 && (
            <ul className="base-list bg-dark-gray pa10">
              {taxInvoiceContactList.map((item: ContractorNoti) => (
                <li key={item.email} className="font14 mb5">
                  {item.email}
                </li>
              ))}
            </ul>
          )}
        </>
      ),
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
    });
  };

  // 빌링 > 이용료 빌링현황 > 청구서 이메일 컬럼 > 이메일 발송
  const sendingBillEmail = async (data: { billId: string; billIssueDate: string; sendHistory: string }) => {
    const { billId, billIssueDate, sendHistory } = data;
    const today = moment().toDate();

    // 발행일이 미래인 경우
    const isFuture = moment(today).isBefore(moment(billIssueDate));

    // 발행일이 오늘 이거나 과거인 경우
    const isPastOrToday = moment(today).isSameOrAfter(moment(billIssueDate));

    // 계약 > 안내문 설정 section 으로 이동
    const path = pagePath.contract.detail.replace(":id", String(contractId)) + `?tab=contract#안내문 설정`;

    const billContactList = contractBaseInfo?.contract?.billContactList?.filter(
      (item: ContractorNoti) => item.receiveNotiType === "TALK_OFFICIALDOC",
    );

    if (isFuture) {
      // 발행일이 미래인 경우 모달 표시
      setBaseModal({
        isOpen: true,
        title: "이메일 청구서는 납부시작일 이후\n발송할 수 있습니다.",
        children: (
          <div className="">
            <Link className="text-underline" to={path} target="_blank">
              {"안내문 설정>청구일 알림"}
            </Link>
            <div className="ic-target-blank"></div>
            <span>을 활성화하면 납부시작일에 자동 발송됩니다.</span>
          </div>
        ),
        btnRightTitle: "확인",
        onClick: () => {
          setBaseModal({ isOpen: false });
        },
      });
    } else if (isPastOrToday) {
      // 발행일이 오늘 이거나 과거인 경우 이메일 발송 모달 표시
      setBaseModal({
        isOpen: true,
        btnLeftTitle: "취소",
        btnRightTitle: "확인",
        onClick: async () => {
          setBaseModal({ isOpen: false });

          try {
            await sendBillEmail({ billId, isSendNow: true });
            setBaseModal({
              isOpen: true,
              title: "발송 요청되었습니다.",
              btnRightTitle: "확인",
              onClick: () => {
                setBaseModal({ isOpen: false });
                onSuccess?.(scheduleId);
              },
            });
          } catch (error) {
            console.error("이메일 발송 실패:", error);
          }
        },
        title: sendHistory !== "" ? "청구서 이메일을 다시 보내시겠습니까?" : "청구서 이메일을 보내시겠습니까?",
        children: (
          <>
            <div className="mb20">
              <Link className="text-underline" to={path} target="_blank">
                {"안내문 설정>이메일"}
              </Link>
              <div className="ic-target-blank"></div>
              <span>의 정보를 확인해주세요.</span>
            </div>
            {billContactList.length > 0 && (
              <ul className="base-list bg-dark-gray pa10">
                {billContactList.map((item: ContractorNoti) => (
                  <li key={item.email} className="font14 mb5">
                    {item.email}
                  </li>
                ))}
              </ul>
            )}
          </>
        ),
        onClose: () => {
          setBaseModal({ isOpen: false });
        },
      });
    }
  };

  return { sendingBillEmail, sendingTaxInvoiceEmail, handleEditTaxPurposeType, openNtsModal, openTaxInvoiceViewUrl };
};

export default useBill;
